import { ReactElement, useId } from 'react';
import { Box, Checkbox } from '@ff-it/ui';
import { DecimalField, TextField, useForm } from '@ff-it/form';
import { RemoveInline } from 'components';
import type { RadioTimeBands } from '../types';
import type { Outlet } from 'modules/supplier/outletOrServiceCategory/types';
import { useFieldValue } from 'hooks';
import { BandRateTable } from '../common/BandRateTable';

type RateCardEditProps = {
  index: number;
  name: string;
  bands: RadioTimeBands;
  outlets: Outlet[];
};

function DefaultRateCardToggle({ name }: { name: string }): ReactElement {
  const currentName = `${name}.default`;
  const isDefault = useFieldValue(currentName);
  const form = useForm();
  const id = useId();
  return (
    <Box display="flex" alignItems="center" ml="auto">
      <Checkbox
        checked={isDefault}
        size="sm"
        variant="radio"
        name={currentName}
        onChange={() => {
          if (!isDefault) {
            form.batch(() => {
              const values: any[] = form.getState()?.values.ratecards || [];
              values.forEach((v, index) => {
                const name = `ratecards[${index}].default`;
                if (name === currentName) {
                  form.change(name, true);
                } else {
                  if (v.default) {
                    form.change(name, false);
                  }
                }
              });
            });
          }
        }}
        id={id}
      />
      <Box as="label" mb="0" ml="2" htmlFor={id} fontWeight="regular" fontSize="sm">
        Default ratecard
      </Box>
    </Box>
  );
}

export function RateCardEdit({ index, name, bands, outlets }: RateCardEditProps): ReactElement {
  return (
    <Box borderBottom="subtle" mb="2">
      <Box mb="2" display="flex">
        <TextField name={`${name}.name`} placeholder="Name" size="sm" className="mb-0" />
        <DefaultRateCardToggle name={name} />
      </Box>
      <BandRateTable
        bands={bands}
        columns={outlets.map(({ id, name: outletName }) => ({
          key: id,
          header: outletName,
          render: (_band, _dayType, bandKey) => (
            <DecimalField
              name={`${name}.rates.${bandKey}._${id}`}
              className="mb-0 text-right"
              size="sm"
              precision={2}
            />
          ),
        }))}
      />
      <Box display="flex" py="2">
        <RemoveInline name="ratecards" index={index} ml="auto" min={1}>
          Remove ratecard
        </RemoveInline>
      </Box>
    </Box>
  );
}
